import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Upload, X, Star, Loader2, Image as ImageIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface PropertyImage {
  id: string;
  file_path: string;
  file_name: string;
  is_primary: boolean;
  display_order: number;
}

interface PropertyImageGalleryProps {
  propertyId: string;
}

export const PropertyImageGallery = ({ propertyId }: PropertyImageGalleryProps) => {
  const [images, setImages] = useState<PropertyImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  useEffect(() => {
    fetchImages();
  }, [propertyId]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("property_images")
        .select("*")
        .eq("property_id", propertyId)
        .order("display_order", { ascending: true });

      if (error) throw error;
      setImages(data || []);
    } catch (error: any) {
      console.error("Error fetching images:", error);
      toast.error("Failed to load images");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Validate file type
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name} is not an image file`);
          continue;
        }

        // Validate file size (5MB)
        if (file.size > 5242880) {
          toast.error(`${file.name} is too large (max 5MB)`);
          continue;
        }

        const fileExt = file.name.split(".").pop();
        const fileName = `${propertyId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

        // Upload to storage
        const { error: uploadError } = await supabase.storage
          .from("property-images")
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from("property-images")
          .getPublicUrl(fileName);

        // Save to database
        const { error: dbError } = await supabase
          .from("property_images")
          .insert({
            property_id: propertyId,
            file_path: publicUrl,
            file_name: file.name,
            display_order: images.length + i,
            is_primary: images.length === 0 && i === 0
          });

        if (dbError) throw dbError;
      }

      toast.success("Images uploaded successfully");
      setShowUploadDialog(false);
      fetchImages();
    } catch (error: any) {
      console.error("Error uploading images:", error);
      toast.error("Failed to upload images");
    } finally {
      setUploading(false);
    }
  };

  const handleSetPrimary = async (imageId: string) => {
    try {
      // Remove primary from all images
      await supabase
        .from("property_images")
        .update({ is_primary: false })
        .eq("property_id", propertyId);

      // Set new primary
      const { error } = await supabase
        .from("property_images")
        .update({ is_primary: true })
        .eq("id", imageId);

      if (error) throw error;

      toast.success("Primary image updated");
      fetchImages();
    } catch (error: any) {
      console.error("Error setting primary image:", error);
      toast.error("Failed to set primary image");
    }
  };

  const handleDeleteImage = async (imageId: string, filePath: string) => {
    if (!confirm("Are you sure you want to delete this image?")) return;

    try {
      // Extract file path from URL
      const pathParts = filePath.split("/property-images/");
      const storagePath = pathParts[1];

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("property-images")
        .remove([storagePath]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from("property_images")
        .delete()
        .eq("id", imageId);

      if (dbError) throw dbError;

      toast.success("Image deleted");
      fetchImages();
    } catch (error: any) {
      console.error("Error deleting image:", error);
      toast.error("Failed to delete image");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Property Images</h3>
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Upload className="h-4 w-4 mr-2" />
              Upload Images
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Property Images</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="images">Select Images (Max 5MB each)</Label>
                <input
                  id="images"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="mt-2 block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                />
              </div>
              {uploading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Uploading images...
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {images.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <ImageIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No images uploaded yet</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => setShowUploadDialog(true)}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload First Image
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((image) => (
            <div key={image.id} className="relative group aspect-video rounded-lg overflow-hidden border">
              <img
                src={image.file_path}
                alt={image.file_name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleSetPrimary(image.id)}
                  disabled={image.is_primary}
                >
                  <Star className={`h-4 w-4 ${image.is_primary ? "fill-yellow-400 text-yellow-400" : ""}`} />
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDeleteImage(image.id, image.file_path)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              {image.is_primary && (
                <Badge className="absolute top-2 left-2 bg-yellow-400 text-black">
                  Primary
                </Badge>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
